import React, { useState, useEffect } from 'react';
import authenticationService from '../../services/authenticationService';

const PasswordPolicy = ({ scopeType, scopeId }) => {
  const [policy, setPolicy] = useState(null);
  const [appDefaults, setAppDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allowPasswordAuth, setAllowPasswordAuth] = useState(true);

  useEffect(() => {
    loadPolicy();
    if (scopeType && scopeId) {
      loadAppDefaults();
    }
  }, [scopeType, scopeId]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      const data = await authenticationService.getPolicy(scopeType, scopeId);
      setPolicy(data.passwordPolicy || {});
      setAllowPasswordAuth(data.allowPasswordAuth !== false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAppDefaults = async () => {
    try {
      const defaults = await authenticationService.getAppDefaults();
      setAppDefaults(defaults.passwordPolicy);
    } catch (err) {
      console.error('Failed to load app defaults:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await authenticationService.updatePasswordPolicy(scopeType, scopeId, policy);
      await authenticationService.updatePasswordAuthSetting(scopeType, scopeId, allowPasswordAuth);

      setSuccess('Password policy updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  if (!policy) {
    return <div>Failed to load password policy</div>;
  }

  return (
    <div>
      {/* Password Authentication Toggle */}
      <div style={{
        padding: '20px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
              Password Authentication
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              {allowPasswordAuth 
                ? 'Users can log in using passwords' 
                : 'Password authentication is disabled. Users must use SSO to log in.'}
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={allowPasswordAuth}
              onChange={(e) => setAllowPasswordAuth(e.target.checked)}
              style={{ width: '20px', height: '20px', marginRight: '8px' }}
            />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Allow Password Auth</span>
          </label>
        </div>
      </div>

      {/* App-level minimums warning */}
      {appDefaults && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '14px',
          color: '#92400e'
        }}>
          <strong>App-level minimums:</strong> Minimum length: {appDefaults.minLength} chars, 
          Uppercase: {appDefaults.requireUppercase ? 'Required' : 'Optional'}, 
          Lowercase: {appDefaults.requireLowercase ? 'Required' : 'Optional'}, 
          Digit: {appDefaults.requireDigit ? 'Required' : 'Optional'}, 
          Special Char: {appDefaults.requireSpecialChar ? 'Required' : 'Optional'}
        </div>
      )}

      {/* Error/Success messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '8px',
          color: '#065f46',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Policy Form */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
          Password Requirements
        </h3>

        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Minimum Length */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Minimum Length: {policy.minLength || 8} characters
            </label>
            <input
              type="range"
              min={appDefaults?.minLength || 8}
              max={64}
              value={policy.minLength || 8}
              onChange={(e) => setPolicy({ ...policy, minLength: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              <span>{appDefaults?.minLength || 8}</span>
              <span>64</span>
            </div>
          </div>

          {/* Complexity Requirements */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
              Complexity Requirements
            </label>
            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireUppercase !== false}
                  onChange={(e) => setPolicy({ ...policy, requireUppercase: e.target.checked })}
                  disabled={appDefaults?.requireUppercase}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>
                  Require uppercase letter {appDefaults?.requireUppercase && '(App minimum)'}
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireLowercase !== false}
                  onChange={(e) => setPolicy({ ...policy, requireLowercase: e.target.checked })}
                  disabled={appDefaults?.requireLowercase}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>
                  Require lowercase letter {appDefaults?.requireLowercase && '(App minimum)'}
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireDigit !== false}
                  onChange={(e) => setPolicy({ ...policy, requireDigit: e.target.checked })}
                  disabled={appDefaults?.requireDigit}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>
                  Require digit {appDefaults?.requireDigit && '(App minimum)'}
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireSpecialChar !== false}
                  onChange={(e) => setPolicy({ ...policy, requireSpecialChar: e.target.checked })}
                  disabled={appDefaults?.requireSpecialChar}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>
                  Require special character {appDefaults?.requireSpecialChar && '(App minimum)'}
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.allowPassphrase || false}
                  onChange={(e) => setPolicy({ ...policy, allowPassphrase: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Allow passphrases (spaces and longer passwords)</span>
              </label>
            </div>
          </div>

          {/* Expiration */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Password Expiration (days)
            </label>
            <input
              type="number"
              min={0}
              max={365}
              value={policy.expirationDays || 0}
              onChange={(e) => setPolicy({ ...policy, expirationDays: parseInt(e.target.value) || 0 })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {policy.expirationDays === 0 
                ? 'Passwords never expire (recommended)' 
                : `Passwords expire after ${policy.expirationDays} days`}
            </p>
          </div>

          {/* Warning Days */}
          {policy.expirationDays > 0 && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Warning Days Before Expiration
              </label>
              <input
                type="number"
                min={1}
                max={30}
                value={policy.warningDays || 7}
                onChange={(e) => setPolicy({ ...policy, warningDays: parseInt(e.target.value) || 7 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          )}

          {/* Password History */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Password History Count
            </label>
            <input
              type="number"
              min={0}
              max={24}
              value={policy.historyCount || 5}
              onChange={(e) => setPolicy({ ...policy, historyCount: parseInt(e.target.value) || 5 })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Prevent reuse of last {policy.historyCount || 5} passwords
            </p>
          </div>

          {/* Lockout Settings */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Account Lockout</h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Lockout Threshold (failed attempts)
              </label>
              <input
                type="number"
                min={3}
                max={10}
                value={policy.lockoutThreshold || 5}
                onChange={(e) => setPolicy({ ...policy, lockoutThreshold: parseInt(e.target.value) || 5 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Lockout Duration (minutes)
              </label>
              <input
                type="number"
                min={5}
                max={1440}
                value={policy.lockoutDurationMinutes || 30}
                onChange={(e) => setPolicy({ ...policy, lockoutDurationMinutes: parseInt(e.target.value) || 30 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 24px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordPolicy;

